/**
 * AdminPatientsPage — iter1017
 *
 * Dedicated patient roster for admins. One-click "Влез като пациент"
 * (full-access impersonation) with prominent search, last-activity hint,
 * plan badge and audit-safe confirmation. This is the answer to the
 * clinician's ask: "Какъв е пътя за достъп до акаунт на пациент?"
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Search, LogIn, ShieldAlert, ArrowLeft, RefreshCw, Loader2,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPatientsPage() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [pendingId, setPendingId] = useState(null);

  useEffect(() => {
    if (!user) return;
    if ((user.role || "").toLowerCase() !== "admin") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`);
      const rows = (res.data?.users || [])
        .filter((u) => (u.role || "user") !== "admin")
        .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
      setUsers(rows);
    } catch (e) {
      toast.error("Неуспешно зареждане на пациентите");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const personal = ((u.profile || {}).personal || {});
      const first = String(personal.firstName || "").toLowerCase();
      const last = String(personal.lastName || "").toLowerCase();
      return name.includes(q) || email.includes(q) || first.includes(q) || last.includes(q);
    });
  }, [users, query]);

  const impersonate = async (target) => {
    const label = target.name || target.email || target.id;
    const ok = window.confirm(
      `Влизане като ${label}?\n\n` +
      `Ще виждаш точно това, което вижда пациентът. ` +
      `Действието се записва в audit лога.`,
    );
    if (!ok) return;
    setPendingId(target.id);
    try {
      // Preserve the CURRENT admin token so we can restore it after "Изход".
      const currentAdminToken =
        sessionStorage.getItem("nutramilo_access_token")
        || localStorage.getItem("nutramilo_access_token")
        || localStorage.getItem("access_token")
        || "";
      const currentAdminRefresh = sessionStorage.getItem("nutramilo_refresh_token") || "";
      const res = await axios.post(`${API}/admin/users/${target.id}/impersonate`, {});
      const { access_token, target_user } = res.data || {};
      if (!access_token) throw new Error("Няма получен токен");
      // Store admin tokens for restore.
      localStorage.setItem("impersonation_admin_token", currentAdminToken);
      localStorage.setItem("impersonation_admin_refresh", currentAdminRefresh);
      localStorage.setItem("impersonation_target_email", target_user?.email || target.email || "");
      localStorage.setItem("impersonation_active", "true");
      localStorage.setItem("impersonation_readonly", "false");
      localStorage.setItem("impersonation_return_path", "/admin/patients");
      // Overwrite the active tokens. The impersonation JWT is 1h-lived and
      // has NO refresh token — so clear sessionStorage refresh to prevent
      // any silent refresh from swapping the session back to admin.
      localStorage.setItem("access_token", access_token);
      sessionStorage.setItem("nutramilo_access_token", access_token);
      sessionStorage.removeItem("nutramilo_refresh_token");
      window.location.href = "/dashboard";
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Грешка";
      toast.error("Грешка при impersonation: " + msg);
      setPendingId(null);
    }
  };

  const wrapCls = isDark ? "bg-[hsl(222,47%,8%)] text-gray-100" : "bg-stone-50 text-stone-900";
  const cardCls = isDark ? "bg-white/[0.04] border-white/10" : "bg-white border-stone-200";
  const inputCls = isDark
    ? "bg-white/[0.04] border-white/10 text-white placeholder:text-gray-500"
    : "bg-white border-stone-200 text-stone-900 placeholder:text-stone-400";

  return (
    <div className={`min-h-screen ${wrapCls}`} data-testid="admin-patients-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <button
              onClick={() => navigate("/admin")}
              className={`inline-flex items-center gap-1.5 text-xs mb-2 opacity-70 hover:opacity-100`}
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Обратно към Админ панел
            </button>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Пациенти
            </h1>
            <p className="text-sm opacity-70 mt-1 max-w-2xl">
              Отвори акаунта на всеки пациент, за да провериш какво вижда след
              изчисленията на AI. Достъпът е с full access и се записва в audit лога.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-stone-300 dark:border-white/10 hover:bg-stone-100 dark:hover:bg-white/10 disabled:opacity-50"
            data-testid="refresh-patients-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Обнови
          </button>
        </div>

        {/* Security note */}
        <div
          className={`mb-4 p-3 rounded-xl border flex items-start gap-2 text-xs ${
            isDark ? "bg-amber-500/10 border-amber-500/30 text-amber-200" : "bg-amber-50 border-amber-200 text-amber-900"
          }`}
          data-testid="admin-patients-audit-notice"
        >
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>GDPR audit:</strong> Всяко „Влез като" се записва в
            <code className="mx-1 px-1 py-0.5 rounded bg-black/10">impersonation_audit</code>
            с email на админ, email на пациент, IP и user-agent. Използвайте
            само за клинична проверка на AI резултатите.
          </div>
        </div>

        {/* Search */}
        <div className={`mb-3 flex items-center gap-2 rounded-xl px-3 py-2 border ${cardCls}`}>
          <Search className="w-4 h-4 opacity-60" />
          <input
            type="search"
            placeholder="Търси по име или email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={`flex-1 bg-transparent outline-none text-sm ${inputCls} border-0`}
            data-testid="patients-search-input"
          />
          <span className="text-[11px] opacity-60">{filtered.length} / {users.length}</span>
        </div>

        {/* List */}
        {loading ? (
          <div className={`p-8 rounded-xl border text-center text-sm opacity-70 ${cardCls}`} data-testid="patients-loading">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Зарежда пациентите…
          </div>
        ) : filtered.length === 0 ? (
          <div className={`p-8 rounded-xl border text-center text-sm opacity-70 ${cardCls}`}>
            Няма съвпадащи пациенти.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="patients-list">
            {filtered.map((u) => {
              const personal = (u.profile || {}).personal || {};
              const displayName = [personal.firstName, personal.lastName].filter(Boolean).join(" ") || u.name || u.email;
              const plan = ((u.subscription || {}).plan || "trial").toString();
              const active = ((u.subscription || {}).status || "").toString();
              const created = u.created_at ? new Date(u.created_at).toLocaleDateString("bg-BG") : "—";
              return (
                <li
                  key={u.id}
                  className={`p-3 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${cardCls}`}
                  data-testid={`patient-row-${u.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-bold truncate">{displayName}</div>
                    <div className="text-[11px] opacity-70 truncate">{u.email}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5 text-[10px]">
                      <span className={`px-1.5 py-0.5 rounded-full font-semibold ${
                        active === "active" ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"
                        : active === "trialing" ? "bg-sky-500/20 text-sky-700 dark:text-sky-300"
                        : "bg-stone-500/20 text-stone-700 dark:text-stone-300"
                      }`}>
                        {plan} · {active || "—"}
                      </span>
                      <span className="opacity-60">Регистрация: {created}</span>
                      {u.clinician_status && (
                        <span className="px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-700 dark:text-purple-300 font-semibold">
                          {u.clinician_status}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => impersonate(u)}
                    disabled={pendingId === u.id}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-fuchsia-600 hover:bg-fuchsia-500 text-white shadow-sm disabled:opacity-50 shrink-0"
                    data-testid={`open-as-patient-btn-${u.id}`}
                  >
                    {pendingId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5" />}
                    Отвори като пациент
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
